import { useState } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const MultiImageUpload = ({ 
  images = [], 
  onImagesChange, 
  uploadEndpoint, 
  maxImages = 5,
  label = "Upload Images"
}) => {
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState(images.map(url => ({ url, uploaded: true })));

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if adding these files would exceed the limit
    if (previews.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    const newImageUrls = [];

    try {
      for (const file of files) {
        // Validate file size (50MB max - server will resize)
        if (file.size > 50 * 1024 * 1024) {
          toast.error(`"${file.name}" is too large. Maximum 50MB per image.`);
          continue;
        }

        // Create preview
        const reader = new FileReader();
        const previewPromise = new Promise((resolve) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.readAsDataURL(file);
        });
        const previewUrl = await previewPromise;
        
        // Add to previews temporarily
        setPreviews(prev => [...prev, { url: previewUrl, uploaded: false }]);

        // Upload file
        const formData = new FormData();
        formData.append('file', file);
        
        const token = localStorage.getItem("token");
        const response = await fetch(`${BACKEND_URL}${uploadEndpoint}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (!response.ok) throw new Error('Upload failed');
        
        const data = await response.json();
        newImageUrls.push(data.image_url);
      }

      // Update with actual uploaded URLs
      const allUrls = [...images, ...newImageUrls];
      setPreviews(allUrls.map(url => ({ url, uploaded: true })));
      onImagesChange(allUrls);
      
      toast.success(`${files.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload some images");
      // Revert to original state
      setPreviews(images.map(url => ({ url, uploaded: true })));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setPreviews(newImages.map(url => ({ url, uploaded: true })));
    onImagesChange(newImages);
    toast.success("Image removed");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          {label} ({previews.length}/{maxImages})
        </label>
        {previews.length < maxImages && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={uploading}
            onClick={() => document.getElementById('multi-image-input').click()}
            className="gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Add Images
              </>
            )}
          </Button>
        )}
      </div>

      <input
        id="multi-image-input"
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      {previews.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              <img
                src={preview.url.startsWith('http') ? preview.url : `${BACKEND_URL}${preview.url}`}
                alt={`Upload ${index + 1}`}
                className={`w-full h-32 object-cover rounded-lg border-2 ${
                  preview.uploaded ? 'border-cyan-400/50' : 'border-gray-300 opacity-50'
                }`}
              />
              {preview.uploaded && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {!preview.uploaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {previews.length === 0 && (
        <div 
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-cyan-400 transition-colors"
          onClick={() => document.getElementById('multi-image-input').click()}
        >
          <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm text-gray-600">Click to upload images</p>
          <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP up to 10MB each</p>
        </div>
      )}
    </div>
  );
};

export default MultiImageUpload;
